import { useState, useEffect, useRef, memo } from "react";
import { useContext, useMemo } from "react";
import LevelTransmitterIndicator from "../components/LevelTransmitterIndicator";
import Transmitter from "../components/Transmitter";
import ControlValveControlPanel from "../components/ControlValveControlPanel";
import Valve from "../components/Valve";
import "./PreHeatTrain2.css";
import { WellContext } from "../context/WellContext";
import { AlarmContext } from "../context/AlarmContext";
import { PumpContext } from "../context/PumpContext";
import { ProcessContext } from "../context/ProcessContext";
import Pump from "../components/Pump"; 
import PumpFaceplate from "../components/PumpFaceplate";
import MDSFaceplate from "../components/MDSFaceplate";
import { PlantStore } from "../engine/PlantStore";
import { usePlantStore } from "../engine/usePlantStore";
import { trackPageVisit } from "../competency/PageTracker";
import TransmitterFaceplate from "../components/TransmitterFaceplate";





function ValveOutput({
    tag,
    value,
    x,
    y,
    r = 0,
    description = "Control Valve Opening"
}) {

    const [hover, setHover] = useState(false);
    const [showFaceplate, setShowFaceplate] = useState(false);

    return (
        <>
            {/* Hover */}
            {hover && (
                <div
                    style={{
                        position: "absolute",
                        left: x + 30,
                        top: y - 5,
                        background: "rgba(0,80,180,.85)",
                        color: "#fff",
                        padding: "5px 8px",
                        borderRadius: "4px",
                        border: "1px solid #4fc3ff",
                        fontSize: "11px",
                        zIndex: 9999,
                        pointerEvents: "none",
                        whiteSpace: "nowrap"
                    }}
                >
                    <strong>{tag}</strong><br />
                    {description}
                </div>
            )}

            {/* Original Valve Output */}
            <div
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onClick={() => setShowFaceplate(true)}
                style={{
                    position: "absolute",
                    left: x,
                    top: y,

                    color: "lime",
                    background: "black",

                    fontSize: "10px",
                    padding: "1px 4px",

                    borderRadius: "4px",
                    border: "1px solid #555",

                    transform: `rotate(${r}deg)`,
                    transformOrigin: "center",

                    cursor: "pointer",

                    fontWeight: "bold",

                    minWidth: "22px",
                    textAlign: "center",

                    transition: "0.2s",
                    boxShadow: hover
                        ? "0 0 8px rgba(0,255,255,.7)"
                        : "none"
                }}
            >
                {Number(value).toFixed(1)} %
            </div>

            {showFaceplate && (
                <TransmitterFaceplate
                    tag={tag}
                    value={Number(value)}
                    unit="%"
                    min={0}
                    max={100}
                    HH={100}
                    H={90}
                    L={10}
                    LL={0}
                    description={description}
                    x={x + 35}
                    y={y + 20}
                    onClose={() => setShowFaceplate(false)}
                />
            )}
        </>
    );
}




function PreHeatTrain2() {
  // ✅ Use ref to track render count
  const renderCount = useRef(0);
  renderCount.current += 1;
  console.log(`PreHeatTrain2 Render: ${renderCount.current}`);

  // ✅ Track page visit only once
  useEffect(() => {
    trackPageVisit("PreHeatTrain2");
  }, []);

  const {processData,  setProcessData} = useContext(ProcessContext);
  const { toggleValve, valves } = useContext(WellContext);
  const [hoveredValve, setHoveredValve] = useState(null);

  const DEFAULT_VALVES = {
    FCV_11001: { mode: "Manual", opening: 0, sp: "", pv: "" },
  };

  const [valveData, setValveData] = useState(() => {
    const saved = localStorage.getItem("PreHeatTrain2_valveData");
    if (!saved) return DEFAULT_VALVES;
    try {
      const parsed = JSON.parse(saved);
      return {
        FCV_11001: { ...DEFAULT_VALVES.FCV_11001, ...parsed.FCV_11001 },
      };
    } catch {
      return DEFAULT_VALVES;
    }
  });

  /* ✅ Persist */
  useEffect(() => {
    localStorage.setItem("PreHeatTrain2_valveData", JSON.stringify(valveData));
  }, [valveData]);

  const updateValveData = (tag, field, value) => {
    setValveData(prev => ({
      ...prev,
      [tag]: {
        ...(prev[tag] || DEFAULT_VALVES[tag]),
        [field]: field === "opening" ? Number(value) || 0 : value
      }
    }));
  };

  /* ================= PANEL CONTROL ================= */
  const [openPanels, setOpenPanels] = useState([]);
  const openPanel = (tag) => {
    if (!openPanels.includes(tag)) {
      setOpenPanels(prev => [...prev, tag]);
    }
  };
  const closePanel = (tag) => {
    setOpenPanels(prev => prev.filter(p => p !== tag));
  };

  // ================= PUMP PANEL LOGIC =================
  const [openPumpPanels, setOpenPumpPanels] = useState([]);
  const openPumpPanel = (tag) => {
    if (!openPumpPanels.includes(tag)) {
      setOpenPumpPanels(prev => [...prev, tag]);
    }
  };
  const closePumpPanel = (tag) => {
    setOpenPumpPanels(prev => prev.filter(p => p !== tag));
  };

  const tagStyle = {
    position: "absolute",
    top: -30,
    left: "50%",
    transform: "translateX(-50%)",
    background: "black",
    color: "#00ffcc",
    fontSize: "13px",
    fontWeight: "bold",
    padding: "3px 8px",
    borderRadius: "4px",
    border: "1px solid #555",
    whiteSpace: "nowrap",
    zIndex: 999
  };

  // ================= RETREIVING OF DATA FROM OTHER PAGES VIA LOCAL STROAGE =================
  const { transmitters } = usePlantStore();
  const TT_14005 = transmitters.TT_14005 || 0;
  const TT_14009 = transmitters.TT_14009 || 0;
  const FIC_12011 = transmitters.FIC_12011 || 0;
  const P101_Running = transmitters.P101_Running || 0;
  const TT_19013 = transmitters.TT_19013 || 0;

  const xv11001Open = !valves?.XV_11001;
  const xv11002Open = !valves?.XV_11002;

  const FIC_11011 = FIC_12011;
  const TT_11001 = xv11002Open ? TT_14005 : 22; // TT_14005 (add this later)
  const TT_11002 = TT_19013;
  const TT_11005 = xv11001Open ? TT_14009 : 22; // TT_14009 (add this later)

  
  

/* =========================================================
   E-103A/B DIESEL ↔ CRUDE HEAT EXCHANGER
   PROCESS CONTEXT VERSION
========================================================= */

const TT_11003 = processData.TT_11003;
const TT_11004 = processData.TT_11004;


/* =========================================================
   REFS
========================================================= */

const E103_intervalRef = useRef(null);
const E103_isMounted = useRef(false);

const E103_processRef = useRef({});


/* =========================================================
   KEEP PROCESS VALUES UPDATED
========================================================= */

useEffect(() => {

  E103_processRef.current = {

    TT_11001,
    TT_19013,
    xv11002Open

  };

});


/* =========================================================
   E-103 HEAT EXCHANGER
========================================================= */

useEffect(() => {

  if (E103_isMounted.current) return;

  E103_isMounted.current = true;

  console.log("✅ E-103 heat exchanger interval created");

  E103_intervalRef.current = setInterval(() => {

    const {

      TT_11001,
      TT_19013,
      xv11002Open

    } = E103_processRef.current;


    /* =====================================
       FLOW CONDITIONS
    ===================================== */

    const dieselFlow =
      xv11002Open;

    const crudeFlow =
      Number(TT_19013) > 5;


    /* =====================================
       INLET TEMPERATURES
    ===================================== */

    const dieselInlet =
      dieselFlow
        ? Number(TT_11001) || 32
        : 32;

    const crudeInlet =
      crudeFlow
        ? Number(TT_19013) || 32
        : 32;


    let targetTT11003 =
      dieselInlet;

    let targetTT11004 =
      crudeInlet;


    /* =====================================
       DETERMINE HOT/COLD STREAM
    ===================================== */

    const hotter =
      Math.max(dieselInlet, crudeInlet);

    const colder =
      Math.min(dieselInlet, crudeInlet);

    const deltaT =
      hotter - colder;


    /* =====================================
       HEAT EXCHANGE
    ===================================== */

    if (
      dieselFlow &&
      crudeFlow &&
      deltaT > 0.05
    ) {

      /*
         Realistic exchanger effectiveness
      */

      const duty =
        Math.max(
          0.50,
          deltaT * 0.55
        );

      /*
         Crude has lower heat capacity
         in this model, therefore it
         gains temperature faster.
      */

      const secondaryDuty =
        Math.min(
          20,
          duty * 0.40
        );

      if (dieselInlet >= crudeInlet) {

        targetTT11003 =
          dieselInlet - duty;

        targetTT11004 =
          crudeInlet + secondaryDuty;

      }

      else {

        targetTT11003 =
          dieselInlet + secondaryDuty;

        targetTT11004 =
          crudeInlet - duty;

      }

    }


    /* =====================================
       PHYSICAL LIMITS
    ===================================== */

    if (dieselInlet >= crudeInlet) {

      targetTT11003 =
        Math.max(
          targetTT11003,
          crudeInlet + 8
        );

      targetTT11004 =
        Math.min(
          targetTT11004,
          dieselInlet - 8
        );

    }

    else {

      targetTT11004 =
        Math.max(
          targetTT11004,
          dieselInlet + 8
        );

      targetTT11003 =
        Math.min(
          targetTT11003,
          crudeInlet - 8
        );

    }


    /* =====================================
       UPDATE PROCESS DATA
    ===================================== */

    setProcessData(prev => {

      const currentTT11003 =

        (typeof prev.TT_11003 === "number" &&
         prev.TT_11003 > 5)

        ? prev.TT_11003

        : dieselInlet;


      const currentTT11004 =

        (typeof prev.TT_11004 === "number" &&
         prev.TT_11004 > 5)

        ? prev.TT_11004

        : crudeInlet;


      return {

        ...prev,

        TT_11003:

          Number(

            (

              currentTT11003 +

              (

                targetTT11003 -

                currentTT11003

              ) * 0.08

            ).toFixed(2)

          ),

        TT_11004:

          Number(

            (

              currentTT11004 +

              (

                targetTT11004 -

                currentTT11004

              ) * 0.12

            ).toFixed(2)

          )

      };

    });

  }, 1000);


  return () => {

    console.log("❌ E-103 interval cleared");

    if (E103_intervalRef.current) {

      clearInterval(E103_intervalRef.current);

      E103_intervalRef.current = null;

    }

    E103_isMounted.current = false;

  };

}, []);





  /* =========================================================
   E-104A/B ATMOSPHERIC RESIDUE ↔ CRUDE EXCHANGER
========================================================= */

const TT_11006 = processData.TT_11006;
const TT_11007 = processData.TT_11007;


/* =========================================================
   REFS
========================================================= */

const E104_intervalRef = useRef(null);
const E104_isMounted = useRef(false);

const E104_processRef = useRef({});


/* =========================================================
   KEEP LIVE VALUES UPDATED
========================================================= */

useEffect(() => {

  E104_processRef.current = {

    TT_11005,
    TT_11004,
    xv11001Open

  };

});


/* =========================================================
   E-104 HEAT EXCHANGER
========================================================= */

useEffect(() => {

  if (E104_isMounted.current) return;

  E104_isMounted.current = true;

  console.log("✅ E-104 interval created");

  E104_intervalRef.current = setInterval(() => {

    const {

      TT_11005,
      TT_11004,
      xv11001Open

    } = E104_processRef.current;


    /* =====================================
       INLETS
    ===================================== */

    const residueFlow = xv11001Open;

    const stream1 =
      residueFlow
        ? Number(TT_11005) || 25
        : 25;

    const stream2 =
      Number(TT_11004) || 25;


    let targetTT11006 = stream1;
    let targetTT11007 = stream2;


    /* =====================================
       DETERMINE HOT/COLD STREAM
    ===================================== */

    const hotter =
      Math.max(stream1, stream2);

    const colder =
      Math.min(stream1, stream2);

    const deltaT =
      hotter - colder;


    /* =====================================
       HEAT EXCHANGE
    ===================================== */

    if (
      residueFlow &&
      deltaT > 0.05
    ) {

      /*
         Heavy residue exchanger
         Large duty
      */

      const effectiveness = 0.35;

      const duty = Math.max(
        0.30,
        deltaT * effectiveness
      );

      /*
         Crude receives less heat than
         residue loses
      */

      const secondaryDuty =
        duty * 0.42;

      if (stream1 >= stream2) {

        // Residue hotter

        targetTT11006 =
          stream1 - duty;

        targetTT11007 =
          stream2 + secondaryDuty;

      }

      else {

        // Crude hotter

        targetTT11006 =
          stream1 + secondaryDuty;

        targetTT11007 =
          stream2 - duty;

      }

    }


    /* =====================================
       PHYSICAL LIMITS
    ===================================== */

    if (stream1 >= stream2) {

      targetTT11006 =
        Math.max(
          targetTT11006,
          stream2 + 1
        );

      targetTT11007 =
        Math.min(
          targetTT11007,
          stream1 - 1
        );

    }

    else {

      targetTT11007 =
        Math.max(
          targetTT11007,
          stream1 + 1
        );

      targetTT11006 =
        Math.min(
          targetTT11006,
          stream2 - 1
        );

    }


    /* =====================================
       THERMAL LAG
    ===================================== */

    setProcessData(prev => ({

      ...prev,

      TT_11006: Number(

        (

          (prev.TT_11006 ?? stream1)

          +

          (

            targetTT11006 -

            (prev.TT_11006 ?? stream1)

          ) * 0.08

        ).toFixed(2)

      ),

      TT_11007: Number(

        (

          (prev.TT_11007 ?? stream2)

          +

          (

            targetTT11007 -

            (prev.TT_11007 ?? stream2)

          ) * 0.08

        ).toFixed(2)

      )

    }));

  }, 1000);


  return () => {

    console.log("❌ E-104 interval cleared");

    if (E104_intervalRef.current) {

      clearInterval(E104_intervalRef.current);

      E104_intervalRef.current = null;

    }

    E104_isMounted.current = false;

  };

}, []);










  // ✅ Update PlantStore without causing re-renders
  useEffect(() => {
    const current = PlantStore.get().transmitters;
    if (current.TT_11007 === TT_11007 && current.TT_11006 === TT_11006 && current.TT_11001 === TT_11001) {
      return;
    }

    PlantStore.set({
      transmitters: {
        ...current,
        TT_11007, TT_11006, TT_11001
      }
    });
  }, [TT_11007, TT_11006, TT_11001]);











  return (
    <div className="PreHeatTrain2-container">
      {/* TITLE */}
      <h2 style={{
        position: "absolute",
        top: "0px",
        left: "240px",
        color: "white",
        background: "rgb(30, 5, 250)",
        padding: "2px 373px",
        zIndex: 10,
      }}>
        PRE-HEAT TRAIN 2
      </h2>

      {/* =================SHUTDOWN VALVES ================= */}
      <Valve
        className="XV-11001"
        tag="XV_11001"
        x={1037}
        y={364}
        isClosed={valves.XV_11001}
        toggle={toggleValve}
      />

      <Valve
        className="XV-11002"
        tag="XV_11002"
        x={287}
        y={137}
        isClosed={valves.XV_11002}
        toggle={toggleValve}
      />

      {/* ================= CONTROL VALVE FACEPLATES ================= */}
      {openPanels.map((tag, index) => (
        <ControlValveControlPanel
          key={tag}
          tag={tag}
          data={valveData?.[tag] ?? DEFAULT_VALVES?.[tag]}
          update={updateValveData}
          close={() => closePanel(tag)}
          defaultPosition={{
            x: 300 + index * 40,
            y: 150 + index * 40
          }}
        />
      ))}

      {/* ================= PUMPS CONTROL VALVE FACEPLATES ================= */}
      {openPumpPanels.map((tag, index) => (
        <PumpFaceplate
          key={tag}
          tag={tag}
          close={() => closePumpPanel(tag)}
          defaultPosition={{
            x: 300 + index * 40,
            y: 150 + index * 40
          }}
        />
      ))}

      {/* ================= TRANSMITTERS ================= */}
      <Transmitter
        tag="TT_11001"
        value={[TT_11001]}
        x={142}
        y={124}
        unit="°C"
        max={200}
        HH={186}
        H={165}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF DIESEL FROM E-105A/B"
      />

      <Transmitter
        tag="TT_11002"
        value={[TT_11002]}
        x={442}
        y={71}
        unit="°C"
        max={200}
        HH={186}
        H={165}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CRUDE FROM E-102B TO E-103B"
      />

      <Transmitter
        tag="TT_11003"
        value={[TT_11003]}
        x={1102}
        y={114}
        unit="°C"
        max={200}
        HH={186}
        H={165}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF DIESEL FROM A-103A/B"
      />

      <Transmitter
        tag="TT_11004"
        value={[TT_11004]}
        x={222}
        y={241}
        unit="°C"
        max={200}
        HH={186}
        H={165}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CRUDE FROM E-103A/B TO E-104A/B"
      />

      <Transmitter
        tag="TT_11005"
        value={[TT_11005]}
        x={1122}
        y={351}
        unit="°C"
        max={200}
        HH={186}
        H={165}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF ATMOSPHERIC RESIDUE FROM E-107A/B"
      />

      <Transmitter
        tag="TT_11006"
        value={[TT_11006]}
        x={1002}
        y={573}
        unit="°C"
        max={200}
        HH={186}
        H={165}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF ATMOSPHERIC RESIDUE FROM E-104A/B TO E-117 A/B"
      />

      <Transmitter
        tag="TT_11007"
        value={[TT_11007]}
        x={1002}
        y={305}
        unit="°C"
        max={200}
        HH={186}
        H={165}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CRUDE FROM E-104A/B TO V-201"
      />
    </div>
  );
}

export default memo(PreHeatTrain2);